import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { GENESYS } from "../config/genesys";
import { getStatusMeta } from "../config/statuses";
import Footer from "../components/Footer";
import AuroraWave from "../components/AuroraWave";

/**
 * DEBSUN GENESYS public page.
 * Describes problem, vision, industries, future direction and status only.
 * Never reveals architecture or proprietary implementation detail.
 */
export default function GenesysPage() {
  const status = getStatusMeta(GENESYS.status);

  return (
    <div className="min-h-screen pt-28 relative overflow-hidden" data-testid="genesys-page">
      <AuroraWave
        className="absolute left-[-10%] right-[-10%] top-24 h-[320px] w-[120%] pointer-events-none"
        opacity={0.35}
        variant="thin"
      />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative pb-24">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">
          § {GENESYS.hero.eyebrow}
        </div>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <h1 className="display display-hero text-5xl md:text-7xl tracking-[-0.03em]">
            {GENESYS.hero.title}
          </h1>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
            <span
              className="font-mono-2 text-[11px] tracking-[0.2em] uppercase"
              style={{ color: status.color }}
            >
              {status.label}
            </span>
          </div>
        </div>
        <p className="mt-4 text-lg md:text-xl text-steel-text leading-relaxed max-w-3xl">
          {GENESYS.hero.subtitle}
        </p>
        <p className="mt-4 text-base text-steel-text leading-relaxed max-w-3xl">
          {GENESYS.vision.body[0]}
        </p>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="steel-card clip-frame p-8 lg:p-10"
            data-testid="genesys-problem"
          >
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
              § {GENESYS.problem.title}
            </div>
            {GENESYS.problem.body.map((p) => (
              <p key={p} className="mt-4 text-sm text-steel-text leading-relaxed first:mt-0">
                {p}
              </p>
            ))}
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="steel-card clip-frame p-8 lg:p-10"
            data-testid="genesys-vision"
          >
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
              § {GENESYS.vision.title}
            </div>
            {GENESYS.vision.body.map((p) => (
              <p key={p} className="mt-4 text-sm text-steel-text leading-relaxed first:mt-0">
                {p}
              </p>
            ))}
          </motion.section>
        </div>

        <section className="mt-16" data-testid="genesys-industries">
          <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-5">
            § Industries
          </div>
          <div className="flex flex-wrap gap-2">
            {GENESYS.industries.map((industry) => (
              <span
                key={industry}
                className="font-mono-2 text-[11px] tracking-[0.15em] uppercase px-3 py-2 border border-border text-steel-text"
              >
                {industry}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-20" data-testid="genesys-capabilities">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-6">
              <h2 className="display display-section text-3xl md:text-5xl">
                Future <span style={{ color: "var(--gold)" }}>capabilities</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-sm text-steel-text leading-relaxed">
                Directional capabilities under research and development. Nothing below
                implies present commercial availability.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GENESYS.futureCapabilities.map((cap, i) => (
              <motion.div
                key={cap.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                className="border border-border bg-secondary/30 p-6"
                data-testid={`genesys-capability-${cap.id}`}
              >
                <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="display display-section text-lg">{cap.name}</h3>
                <p className="mt-3 text-sm text-steel-text leading-relaxed">
                  {cap.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className="border border-border p-8" data-testid="genesys-future-direction">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
              § {GENESYS.futureDirection.title}
            </div>
            {GENESYS.futureDirection.body.map((p) => (
              <p key={p} className="mt-3 text-sm text-steel-text leading-relaxed">
                {p}
              </p>
            ))}
          </section>

          <section className="border border-border p-8 bg-secondary/40" data-testid="genesys-status">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
              § {GENESYS.currentStatus.title}
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.color }} />
              <span
                className="font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                style={{ color: status.color }}
              >
                {GENESYS.currentStatus.label}
              </span>
            </div>
            {GENESYS.currentStatus.body.map((p) => (
              <p key={p} className="mt-3 text-sm text-steel-text leading-relaxed">
                {p}
              </p>
            ))}
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to={GENESYS.cta.primary.href}
            className="btn-gold clip-frame-sm px-6 py-4 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
            data-testid="genesys-contact-cta"
          >
            {GENESYS.cta.primary.label} <ArrowUpRight size={14} />
          </Link>
          <Link
            to={GENESYS.cta.secondary.href}
            className="btn-ghost clip-frame-sm px-6 py-4 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
            data-testid="genesys-services-cta"
          >
            {GENESYS.cta.secondary.label}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
